export const BLOG_POSTS = [
  {
    id: 1,
    slug: 'design-trends-2025',
    title: 'Design Trends to Watch in 2025',
    excerpt: 'From bold typography to immersive motion design, discover the trends shaping the visual landscape this year.',
    content: `
      <p>The world of design is evolving faster than ever. As we move through 2025, several trends are emerging that every designer should know about.</p>
      
      <h3>1. Bold Typography</h3>
      <p>Designers are pushing boundaries with oversized, expressive typefaces. This trend is about making a statement and creating visual impact.</p>
      
      <h3>2. Immersive Motion Design</h3>
      <p>Motion is no longer an afterthought – it's at the forefront of design. From micro-interactions to full cinematic experiences, motion adds depth and personality.</p>
      
      <h3>3. Sustainable Design</h3>
      <p>Consumers are increasingly drawn to brands that care about the environment. Sustainable design is about using eco-friendly materials and communicating values through visuals.</p>
      
      <h3>4. AI-Assisted Creativity</h3>
      <p>AI tools are becoming essential for designers, helping with ideation, prototyping, and even final assets. The key is using them as a tool, not a replacement.</p>
      
      <p>As a designer, staying ahead of these trends will help you create work that feels fresh, relevant, and impactful.</p>
    `,
    image: 'https://images.unsplash.com/photo-1561070791-2526d30994b5?w=800&h=500&fit=crop&crop=center',
    date: '2025-01-15',
    readTime: '5 min read',
    author: {
      name: 'Xorlali',
      role: 'Creative Producer',
      avatar: 'https://images.unsplash.com/photo-1573497019940-1c28c88b4f3e?w=80&h=80&fit=crop&crop=face',
    },
    category: 'Design Trends',
  },
  {
    id: 2,
    slug: 'brand-identity-crafting',
    title: 'Crafting a Brand Identity That Connects',
    excerpt: 'A strong brand identity is more than just a logo – it\'s the emotional connection between your business and your audience.',
    content: `
      <p>Brand identity is the visual and emotional expression of your business. It's how people perceive you, and it's what makes you memorable.</p>
      
      <h3>Start with Strategy</h3>
      <p>Before you design anything, you need to understand your audience, your values, and your goals. This is the foundation of everything that follows.</p>
      
      <h3>Visual Consistency</h3>
      <p>Every touchpoint – from your logo to your website to your social media – should feel like it belongs to the same brand. Consistency builds trust.</p>
      
      <h3>Telling Your Story</h3>
      <p>Your brand identity should tell your story. Whether you're playful, serious, or somewhere in between, your visuals should reflect that personality.</p>
      
      <p>At the end of the day, a great brand identity is one that resonates with people and builds lasting relationships.</p>
    `,
    image: 'https://images.unsplash.com/photo-1544716278-ca5e3f4abd8c?w=800&h=500&fit=crop&crop=center',
    date: '2025-01-10',
    readTime: '4 min read',
    author: {
      name: 'Xorlali',
      role: 'Creative Producer',
      avatar: 'https://images.unsplash.com/photo-1573497019940-1c28c88b4f3e?w=80&h=80&fit=crop&crop=face',
    },
    category: 'Branding',
  },
  {
    id: 3,
    slug: 'video-storytelling-tips',
    title: 'Video Storytelling: Tips for Creating Engaging Content',
    excerpt: 'Great video content tells a story. Learn how to captivate your audience with visual storytelling techniques.',
    content: `
      <p>Video is one of the most powerful mediums for storytelling. But creating engaging video content requires more than just pressing record.</p>
      
      <h3>Know Your Audience</h3>
      <p>Who are you speaking to? Understanding your audience helps you tailor your message and choose the right tone, style, and format.</p>
      
      <h3>Start with a Strong Hook</h3>
      <p>The first few seconds are crucial. Capture your viewer's attention immediately with an interesting visual or compelling question.</p>
      
      <h3>Show, Don't Tell</h3>
      <p>Video is a visual medium. Instead of explaining, demonstrate. Use visuals, motion, and sound to convey your message.</p>
      
      <h3>Embrace Authenticity</h3>
      <p>People connect with real stories. Don't be afraid to show behind-the-scenes moments or share genuine emotions.</p>
      
      <p>With the right approach, video can be your most impactful storytelling tool.</p>
    `,
    image: 'https://images.unsplash.com/photo-1536240478700-b869070f9279?w=800&h=500&fit=crop&crop=center',
    date: '2025-01-05',
    readTime: '6 min read',
    author: {
      name: 'Xorlali',
      role: 'Creative Producer',
      avatar: 'https://images.unsplash.com/photo-1573497019940-1c28c88b4f3e?w=80&h=80&fit=crop&crop=face',
    },
    category: 'Videography',
  },
  {
    id: 4,
    slug: 'photography-composition',
    title: 'Mastering Composition in Photography',
    excerpt: 'Good composition is the foundation of great photography. Learn the principles that make images visually compelling.',
    content: `
      <p>Composition is how you arrange the elements in your frame. It's what separates a snapshot from a work of art.</p>
      
      <h3>The Rule of Thirds</h3>
      <p>Imagine your frame divided into a 3x3 grid. Placing your subject along these lines or at their intersections creates a more balanced and engaging image.</p>
      
      <h3>Leading Lines</h3>
      <p>Use natural lines in your environment – like roads, fences, or architectural features – to draw the viewer's eye into the scene.</p>
      
      <h3>Framing</h3>
      <p>Use elements like doorways, windows, or foliage to frame your subject. This adds depth and context to your image.</p>
      
      <h3>Negative Space</h3>
      <p>Don't be afraid of empty space. It can help your subject breathe and creates a sense of calm and focus.</p>
      
      <p>Mastering composition is a lifelong journey – but these fundamentals will give you a strong start.</p>
    `,
    image: 'https://images.unsplash.com/photo-1544005313-94ddf0286df2?w=800&h=500&fit=crop&crop=center',
    date: '2024-12-28',
    readTime: '5 min read',
    author: {
      name: 'Xorlali',
      role: 'Creative Producer',
      avatar: 'https://images.unsplash.com/photo-1573497019940-1c28c88b4f3e?w=80&h=80&fit=crop&crop=face',
    },
    category: 'Photography',
  },
  {
    id: 5,
    slug: 'motion-graphics-basics',
    title: 'Motion Graphics: A Beginner\'s Guide',
    excerpt: 'Motion graphics bring static designs to life. Learn the basics of creating animated visuals that captivate.',
    content: `
      <p>Motion graphics are everywhere – from explainer videos to social media ads. They're a powerful way to communicate ideas dynamically.</p>
      
      <h3>What Are Motion Graphics?</h3>
      <p>Motion graphics are animated graphic designs. Unlike traditional animation, they focus on moving text, shapes, and visual elements to tell a story.</p>
      
      <h3>Getting Started</h3>
      <p>You don't need expensive software to start. Tools like After Effects, DaVinci Resolve, and even Canva offer motion capabilities.</p>
      
      <h3>Key Principles</h3>
      <p>Timing, spacing, and easing are essential. They create the sense of movement and make your animations feel polished.</p>
      
      <h3>Applications</h3>
      <p>Motion graphics are used in title sequences, explainer videos, social media content, and even branding.</p>
      
      <p>Start small, experiment, and watch your ideas come to life.</p>
    `,
    image: 'https://images.unsplash.com/photo-1558618666-fcd25c85f83b?w=800&h=500&fit=crop&crop=center',
    date: '2024-12-20',
    readTime: '4 min read',
    author: {
      name: 'Xorlali',
      role: 'Creative Producer',
      avatar: 'https://images.unsplash.com/photo-1573497019940-1c28c88b4f3e?w=80&h=80&fit=crop&crop=face',
    },
    category: 'Motion Graphics',
  },
  {
    id: 6,
    slug: 'photo-editing-essentials',
    title: 'Photo Editing Essentials: From Raw to Polished',
    excerpt: 'Great photos start with great editing. Discover the essential techniques to transform your raw images into stunning visuals.',
    content: `
      <p>Editing is an essential part of the photography process. It's where you refine your vision and bring out the best in your images.</p>
      
      <h3>Start with the Basics</h3>
      <p>Adjust exposure, contrast, and white balance to correct the foundation of your image.</p>
      
      <h3>Color Grading</h3>
      <p>Color grading sets the mood of your photo. Warm tones can feel inviting, while cool tones can feel calm and professional.</p>
      
      <h3>Retouching</h3>
      <p>Use tools like healing brushes and clone stamps to remove blemishes, distractions, and imperfections.</p>
      
      <h3>Sharpening & Exporting</h3>
      <p>Sharpening brings out detail, but don't overdo it. Export your images in the right format and resolution for their intended use.</p>
      
      <p>Editing is a skill that improves with practice. The more you edit, the more you'll develop your unique style.</p>
    `,
    image: 'https://images.unsplash.com/photo-1555529669-e69e7aa0ba9a?w=800&h=500&fit=crop&crop=center',
    date: '2024-12-15',
    readTime: '5 min read',
    author: {
      name: 'Xorlali',
      role: 'Creative Producer',
      avatar: 'https://images.unsplash.com/photo-1573497019940-1c28c88b4f3e?w=80&h=80&fit=crop&crop=face',
    },
    category: 'Photo Editing',
  },
];

// ─── Get unique categories ───
export const getBlogCategories = () => {
  const categories = BLOG_POSTS.map(post => post.category);
  return ['All', ...new Set(categories)];
};

// ─── Get related posts ───
export const getRelatedPosts = (postId, category, limit = 3) => {
  return BLOG_POSTS
    .filter(post => post.id !== postId && post.category === category)
    .slice(0, limit);
};